// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

export type ActiveClaimSession = {
  claimClientId: string;
  tourId: string;
  sourcePlayerId?: string | null;
  sourcePlayerName?: string | null;
  startedRoundId?: string | null;
  inviteToken?: string | null;
  claimAccessToken?: string | null;
  claimedAt?: string | null;
};

type ClaimSessionListener = (claimSession: ActiveClaimSession | null) => void;

const CLAIM_CLIENT_ID_KEY = 'goffy:claim-client-id';
const ACTIVE_CLAIM_KEY = 'goffy:active-claim';

let asyncStorage: AsyncStorageLike | null = null;
let memoryClaimClientId: string | null = null;
let memoryActiveClaim: ActiveClaimSession | null = null;
let loadPromise: Promise<void> | null = null;
const claimSessionListeners = new Set<ClaimSessionListener>();

try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeClaimSession = (value: unknown): ActiveClaimSession | null => {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  const claimClientId = normalizeString(input.claimClientId);
  const tourId = normalizeString(input.tourId);
  if (!claimClientId || !tourId) {
    return null;
  }

  return {
    claimClientId,
    tourId,
    sourcePlayerId: normalizeString(input.sourcePlayerId),
    sourcePlayerName: normalizeString(input.sourcePlayerName),
    startedRoundId: normalizeString(input.startedRoundId),
    inviteToken: normalizeString(input.inviteToken),
    claimAccessToken: normalizeString(input.claimAccessToken),
    claimedAt: normalizeString(input.claimedAt),
  };
};

const notifyClaimSessionListeners = () => {
  claimSessionListeners.forEach((listener) => {
    listener(memoryActiveClaim);
  });
};

const createClaimClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `claim-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
};

const persistClaimClientIdToLocalStorage = (claimClientId: string | null) => {
  const storage = getLocalStorage();
  if (!storage) return;
  if (!claimClientId) {
    storage.removeItem(CLAIM_CLIENT_ID_KEY);
    return;
  }
  storage.setItem(CLAIM_CLIENT_ID_KEY, claimClientId);
};

const persistActiveClaimToLocalStorage = (claim: ActiveClaimSession | null) => {
  const storage = getLocalStorage();
  if (!storage) return;
  if (!claim) {
    storage.removeItem(ACTIVE_CLAIM_KEY);
    return;
  }
  storage.setItem(ACTIVE_CLAIM_KEY, JSON.stringify(claim));
};

const loadFromLocalStorage = () => {
  const storage = getLocalStorage();
  if (!storage) return;

  memoryClaimClientId = normalizeString(storage.getItem(CLAIM_CLIENT_ID_KEY));

  const rawClaim = storage.getItem(ACTIVE_CLAIM_KEY);
  if (!rawClaim) {
    memoryActiveClaim = null;
    return;
  }

  try {
    memoryActiveClaim = normalizeClaimSession(JSON.parse(rawClaim));
  } catch {
    memoryActiveClaim = null;
  }
};

const loadFromAsyncStorage = async () => {
  if (!asyncStorage) return;

  const [claimClientIdRaw, activeClaimRaw] = await Promise.all([
    asyncStorage.getItem(CLAIM_CLIENT_ID_KEY),
    asyncStorage.getItem(ACTIVE_CLAIM_KEY),
  ]);

  memoryClaimClientId = normalizeString(claimClientIdRaw);

  if (!activeClaimRaw) {
    memoryActiveClaim = null;
    return;
  }

  try {
    memoryActiveClaim = normalizeClaimSession(JSON.parse(activeClaimRaw));
  } catch {
    memoryActiveClaim = null;
  }
};

export const loadClaimSession = async () => {
  if (loadPromise) {
    await loadPromise;
    return;
  }

  loadPromise = (async () => {
    // Keyed off actual storage availability, not `window`: React Native defines
    // `window` but no `localStorage`, which used to send native down the web path
    // and leave the AsyncStorage read below unreachable.
    if (getLocalStorage()) {
      loadFromLocalStorage();
      return;
    }
    await loadFromAsyncStorage();
  })();

  try {
    await loadPromise;
  } finally {
    loadPromise = null;
  }
};

export const getClaimClientIdSync = (): string | null => {
  if (memoryClaimClientId) {
    return memoryClaimClientId;
  }
  if (typeof window !== 'undefined') {
    loadFromLocalStorage();
  }
  return memoryClaimClientId;
};

export const ensureClaimClientId = async (): Promise<string> => {
  await loadClaimSession();
  if (memoryClaimClientId) {
    return memoryClaimClientId;
  }

  memoryClaimClientId = createClaimClientId();
  persistClaimClientIdToLocalStorage(memoryClaimClientId);
  if (asyncStorage) {
    await asyncStorage.setItem(CLAIM_CLIENT_ID_KEY, memoryClaimClientId);
  }
  return memoryClaimClientId;
};

export const getActiveClaimSessionSync = (): ActiveClaimSession | null => {
  if (memoryActiveClaim) {
    return memoryActiveClaim;
  }
  if (typeof window !== 'undefined') {
    loadFromLocalStorage();
  }
  return memoryActiveClaim;
};

export const getActiveClaimSession = async (): Promise<ActiveClaimSession | null> => {
  await loadClaimSession();
  return memoryActiveClaim;
};

export const subscribeClaimSession = (listener: ClaimSessionListener): (() => void) => {
  claimSessionListeners.add(listener);
  return () => {
    claimSessionListeners.delete(listener);
  };
};

export const setActiveClaimSession = async (claim: ActiveClaimSession | null): Promise<void> => {
  if (!claim) {
    memoryActiveClaim = null;
    persistActiveClaimToLocalStorage(null);
    if (asyncStorage) {
      await asyncStorage.removeItem(ACTIVE_CLAIM_KEY);
    }
    notifyClaimSessionListeners();
    return;
  }

  const normalized = normalizeClaimSession(claim);
  if (!normalized) {
    return;
  }

  memoryActiveClaim = {
    ...normalized,
    claimedAt: normalized.claimedAt ?? new Date().toISOString(),
  };
  memoryClaimClientId = normalized.claimClientId;

  persistClaimClientIdToLocalStorage(memoryClaimClientId);
  persistActiveClaimToLocalStorage(memoryActiveClaim);
  if (asyncStorage) {
    await Promise.all([
      asyncStorage.setItem(CLAIM_CLIENT_ID_KEY, memoryClaimClientId),
      asyncStorage.setItem(ACTIVE_CLAIM_KEY, JSON.stringify(memoryActiveClaim)),
    ]);
  }
  notifyClaimSessionListeners();
};

export const clearActiveClaimSession = async (): Promise<void> => {
  await setActiveClaimSession(null);
};
